import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { MaskLine, Rise } from "@/components/motion/Reveal";
import { IDS } from "@/lib/testIds";
import { PRACTICE_AREAS, HERITAGE, CHAPTERS, TEAM, TESTIMONIALS, CLIENT_LOGOS, INSIGHTS, MATTERS } from "@/data/site";
import { useLang } from "@/i18n/LangContext";
import Seo from "@/seo/Seo";
import { ldLegalService, ldBreadcrumb } from "@/seo/seoConfig";
import { CyberSpotlight } from "@/components/home/CyberSpotlight";

const HERO_IMG = "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=2400&q=85";
const CHAMBERS_IMG = "https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=2400&q=85";
const CHAMBERS_IMG_2 = "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?auto=format&fit=crop&w=2400&q=85";

export default function HomePage() {
  const { t } = useLang();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <div>
      <Seo
        title="Cyber Crime · Economic Offences · CFD Trading Disputes · VARA · Cross-Border Law Firm"
        description="RPLC UAE — a modern legal & business consultancy at the intersection of Law × Technology × Regulation × Business. Cyber crime & digital fraud, economic offences, CFD and online trading disputes, VARA, UAE PDPL, DIFC, ADGM, DPDPA, AI governance and cross-border corporate counsel across UAE, India and USA."
        path="/"
        jsonLd={[ldLegalService(), ldBreadcrumb([{ name: "Home", path: "/" }])]}
      />
      {/* HERO */}
      <section ref={heroRef} data-testid={IDS.home.hero} className="relative min-h-screen w-full overflow-hidden">
        <motion.div style={{ y, scale }} className="absolute inset-0">
          <img src={HERO_IMG} alt="Dubai skyline at dusk" className="w-full h-full object-cover photo-mono" />
          <div className="absolute inset-0 spotlight" />
        </motion.div>

        <motion.div style={{ opacity }} className="relative z-10 max-w-[1600px] mx-auto px-6 lg:px-12 pt-40 lg:pt-52 pb-24">
          <Rise delay={0.15} y={20}>
            <div className="flex items-center gap-4 mb-10">
              <span className="accent-bar" />
              <span className="eyebrow">{t.hero.eyebrow}</span>
            </div>
          </Rise>

          <h1 className="font-serif font-light tracking-tight leading-[0.9] text-[15vw] lg:text-[10.5vw]">
            <MaskLine delay={0.35}>{t.hero.l1}</MaskLine>
            <MaskLine delay={0.55}>{t.hero.l2a} <span className="italic-accent">{t.hero.l2b}</span></MaskLine>
            <MaskLine delay={0.75}>{t.hero.l3} <span className="italic-accent">{t.hero.l4a}</span>{t.hero.l4b}</MaskLine>
          </h1>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <Rise delay={1.15} className="lg:col-span-6">
              <p className="text-[#A19D94] text-lg leading-relaxed max-w-xl">{t.hero.lead}</p>
            </Rise>
            <Rise delay={1.3} className="lg:col-span-6 flex flex-wrap gap-4 lg:justify-end">
              <Link to="/contact" data-testid={IDS.home.heroCta} className="btn-primary">
                {t.hero.ctaPrimary}
                <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
              <Link to="/practice-areas" className="btn-ghost">
                {t.hero.ctaGhost}
              </Link>
            </Rise>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.7, duration: 1 }} className="absolute bottom-8 left-6 lg:left-12 z-10 flex items-center gap-3 text-[#A19D94]">
          <ArrowDown size={14} strokeWidth={1.25} />
          <span className="chapter-num">{t.hero.scroll}</span>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.7, duration: 1 }} className="absolute bottom-8 right-6 lg:right-12 z-10 chapter-num">
          UAE × India × USA
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section data-testid={IDS.home.marquee} className="border-y border-[#141414] py-6 lg:py-7 marquee-mask bg-[#050505]">
        <div className="marquee-track">
          {[...Array(2)].map((_, i) => (
            <div key={`mq-${i}`} className="flex items-center gap-10 pr-10">
              {t.marquee.map((t2, j) => (
                <div key={`mq-${i}-${j}`} className="flex items-center gap-10">
                  <span className="font-serif italic text-2xl lg:text-3xl text-[#F4F1EB]">{t2}</span>
                  <span className="text-[#C84C35] text-base">✦</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section data-testid={IDS.home.manifesto} className="py-32 lg:py-48 border-b border-[#141414]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <Rise>
            <div className="flex items-center gap-4 mb-16">
              <span className="accent-bar" />
              <span className="eyebrow">{t.manifesto.eyebrow}</span>
            </div>
          </Rise>

          <div className="space-y-16 lg:space-y-24">
            {CHAPTERS.map((c, i) => (
              <div key={c.n} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start">
                <Rise className="lg:col-span-2" y={30}>
                  <div className="big-num text-7xl lg:text-8xl text-[#C84C35]">{c.n}</div>
                </Rise>
                <Rise delay={0.1} className="lg:col-span-6">
                  <h3 className="font-serif text-4xl lg:text-6xl leading-[1] tracking-tight">
                    {c.title.split(" ").map((w, wi) => (
                      <span key={wi} className={wi % 3 === 1 ? "italic-accent" : ""}>{w} </span>
                    ))}
                  </h3>
                </Rise>
                <Rise delay={0.2} className="lg:col-span-4">
                  <p className="text-[#A19D94] text-lg leading-relaxed">{c.body}</p>
                  <div className="chapter-num mt-8">{t.manifesto.chapterOf.replace("{n}", String(i + 1).padStart(2, "0"))}</div>
                </Rise>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARALLAX EDITORIAL BREAK — real chambers photo */}
      <ParallaxBreak img={CHAMBERS_IMG} />

      {/* PRACTICE AREAS */}
      <section data-testid={IDS.home.practice} className="py-32 lg:py-48 border-b border-[#141414]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
            <div>
              <Rise><div className="flex items-center gap-4 mb-8"><span className="accent-bar" /><span className="eyebrow">{t.practice.eyebrow}</span></div></Rise>
              <Rise delay={0.1}>
                <h2 className="font-serif text-5xl lg:text-7xl leading-[0.95] tracking-tight max-w-2xl">
                  {t.practice.title1} <span className="italic-accent">{t.practice.title2}</span> {t.practice.title3}
                </h2>
              </Rise>
            </div>
            <Rise delay={0.2}><Link to="/practice-areas" className="btn-ghost">{t.practice.all} <ArrowUpRight size={14} strokeWidth={1.5} /></Link></Rise>
          </div>

          <div className="border-t border-[#141414]">
            {PRACTICE_AREAS.slice(0, 8).map((p, i) => (
              <Rise key={p.n} delay={i * 0.03}>
                <Link to={`/practice-areas/${p.slug}`} data-testid={`home-practice-${p.n}`} className="pa-row grid grid-cols-12 gap-6 items-center py-8 border-b border-[#141414] group">
                  <div className={`col-span-2 lg:col-span-1 chapter-num ${p.featured ? "text-[#C84C35]" : ""}`}>{p.n}</div>
                  <div className="col-span-10 lg:col-span-5 flex flex-wrap items-center gap-x-4 gap-y-2">
                    <span className="pa-title font-serif text-3xl lg:text-5xl leading-[1]">{p.title}</span>
                    {p.featured && <span data-testid="home-practice-featured-tag" className="chapter-num bg-[#C84C35] text-[#050505] px-2.5 py-1">{t.practice.featured}</span>}
                  </div>
                  <div className="hidden lg:block col-span-5 text-[#A19D94] text-sm leading-relaxed">{p.desc}</div>
                  <div className="col-span-12 lg:col-span-1 flex justify-end text-[#66635C] group-hover:text-[#C84C35] transition-colors">
                    <ArrowUpRight size={22} strokeWidth={1.25} />
                  </div>
                </Link>
              </Rise>
            ))}
          </div>
        </div>
      </section>

      {/* CYBER CRIME SPOTLIGHT */}
      <CyberSpotlight />

      {/* SELECTED MATTERS */}
      <section data-testid="home-matters" className="py-32 lg:py-48 border-b border-[#141414] relative overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
            <div>
              <Rise><div className="flex items-center gap-4 mb-8"><span className="accent-bar" /><span className="eyebrow">{t.matters.eyebrow}</span></div></Rise>
              <Rise delay={0.1}>
                <h2 className="font-serif text-5xl lg:text-7xl leading-[0.95] tracking-tight">
                  {t.matters.title1} <span className="italic-accent">{t.matters.title2}</span>
                </h2>
              </Rise>
              <Rise delay={0.2}>
                <p className="mt-8 text-[#A19D94] text-lg max-w-2xl leading-relaxed">{t.matters.lead}</p>
              </Rise>
            </div>
            <Rise delay={0.3}><Link to="/matters" className="btn-ghost">{t.matters.all} <ArrowUpRight size={14} strokeWidth={1.5} /></Link></Rise>
          </div>

          <div className="border-t border-[#141414]">
            {MATTERS.slice(0, 4).map((m, i) => (
              <Rise key={m.n} delay={i * 0.05}>
                <Link to="/matters" className="grid grid-cols-12 gap-4 items-start py-10 border-b border-[#141414] group hover:pl-6 transition-[padding] duration-700">
                  <div className="col-span-2 lg:col-span-1 chapter-num pt-2">{m.n}</div>
                  <div className="col-span-10 lg:col-span-2 chapter-num">
                    <div className="text-[#66635C]">{m.year}</div>
                    <div className="mt-2 text-[#C84C35]">{m.practice}</div>
                  </div>
                  <div className="col-span-12 lg:col-span-6">
                    <h3 className="font-serif text-2xl lg:text-4xl leading-[1.05] tracking-tight group-hover:text-[#C84C35] transition-colors duration-700">{m.title}</h3>
                  </div>
                  <div className="col-span-9 lg:col-span-2 flex items-start lg:justify-end">
                    <span className="font-serif text-2xl lg:text-3xl italic-accent">{m.value}</span>
                  </div>
                  <div className="col-span-3 lg:col-span-1 flex justify-end text-[#66635C] group-hover:text-[#C84C35] transition-colors">
                    <ArrowUpRight size={20} strokeWidth={1.25} />
                  </div>
                </Link>
              </Rise>
            ))}
          </div>
        </div>
      </section>

      {/* HERITAGE TIMELINE */}
      <section data-testid={IDS.home.heritage} className="py-32 lg:py-48 border-b border-[#141414] relative">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-4">
              <Rise><div className="flex items-center gap-4 mb-8"><span className="accent-bar" /><span className="eyebrow">{t.heritage.eyebrow}</span></div></Rise>
              <Rise delay={0.1}>
                <h2 className="font-serif text-5xl lg:text-6xl leading-[0.95] tracking-tight">
                  {t.heritage.title1}<br /><span className="italic-accent">{t.heritage.title2}</span>
                </h2>
              </Rise>
              <Rise delay={0.2}>
                <p className="mt-8 text-[#A19D94] leading-relaxed">{t.heritage.lead}</p>
              </Rise>
            </div>
            <div className="lg:col-span-8 lg:pl-16 lg:border-l border-[#141414] space-y-16">
              {HERITAGE.map((h, i) => (
                <Rise key={h.year} delay={i * 0.06}>
                  <div className="grid grid-cols-12 gap-6 pb-16 border-b border-[#141414]">
                    <div className="col-span-4 lg:col-span-3">
                      <div className="font-serif text-3xl lg:text-4xl text-[#C84C35] leading-tight">{h.year}</div>
                    </div>
                    <div className="col-span-8 lg:col-span-9">
                      <div className="font-serif text-2xl lg:text-3xl">{h.title}</div>
                      <p className="mt-3 text-[#A19D94] leading-relaxed max-w-xl">{h.body}</p>
                    </div>
                  </div>
                </Rise>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PARTNER SPOTLIGHTS */}
      <section data-testid={IDS.home.team} className="py-32 lg:py-48 border-b border-[#141414]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
            <div>
              <Rise><div className="flex items-center gap-4 mb-8"><span className="accent-bar" /><span className="eyebrow">{t.team.eyebrow}</span></div></Rise>
              <Rise delay={0.1}><h2 className="font-serif text-5xl lg:text-7xl leading-[0.95] tracking-tight">{t.team.title1} <span className="italic-accent">{t.team.title2}</span></h2></Rise>
            </div>
            <Rise delay={0.2}><Link to="/team" className="btn-ghost">{t.team.full} <ArrowUpRight size={14} strokeWidth={1.5} /></Link></Rise>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
            {TEAM.slice(0, 2).map((m, i) => (
              <Rise key={m.name} delay={i * 0.1}>
                <div className="group">
                  <div className="relative overflow-hidden border border-[#141414]">
                    <img src={m.img} alt={m.name} className="w-full h-[560px] object-cover object-top photo-mono group-hover:scale-105 transition-transform duration-1000 ease-out" />
                    <div className="absolute top-4 left-4 chapter-num">0{i + 1} / {TEAM.length}</div>
                  </div>
                  <div className="mt-8 grid grid-cols-12 gap-4">
                    <div className="col-span-12 lg:col-span-8">
                      <div className="eyebrow mb-2">{m.role}</div>
                      <h3 className="font-serif text-4xl leading-tight">{m.name}</h3>
                      <div className="italic-accent text-[#A19D94] text-lg mt-1">{m.focus}</div>
                      <p className="mt-4 text-[#A19D94] leading-relaxed">{m.bio}</p>
                    </div>
                  </div>
                </div>
              </Rise>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid={IDS.home.testimonials} className="py-32 lg:py-48 border-b border-[#141414]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <Rise><div className="flex items-center gap-4 mb-8"><span className="accent-bar" /><span className="eyebrow">{t.testimonials.eyebrow}</span></div></Rise>
            <Rise delay={0.1}>
              <h2 className="font-serif text-5xl lg:text-6xl leading-[0.95] tracking-tight">
                {t.testimonials.title1a} <span className="italic-accent">{t.testimonials.title1b}</span><br />{t.testimonials.title2}
              </h2>
            </Rise>
          </div>
          <div className="lg:col-span-8 space-y-14">
            {TESTIMONIALS.map((tm, i) => (
              <Rise key={i} delay={i * 0.08}>
                <blockquote className="border-l border-[#C84C35] pl-8 lg:pl-12">
                  <p className="font-serif italic text-2xl lg:text-4xl leading-tight text-[#F4F1EB]">&ldquo;{tm.quote}&rdquo;</p>
                  <div className="mt-6 flex items-center gap-3">
                    <span className="chapter-num">{("0" + (i + 1)).slice(-2)}</span>
                    <span className="text-[#F4F1EB]">{tm.name}</span>
                    <span className="text-[#66635C]">— {tm.role}</span>
                  </div>
                </blockquote>
              </Rise>
            ))}
          </div>
        </div>
      </section>

      {/* INSIGHTS PREVIEW */}
      <section className="py-32 lg:py-48 border-b border-[#141414]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
            <div>
              <Rise><div className="flex items-center gap-4 mb-8"><span className="accent-bar" /><span className="eyebrow">{t.insights.eyebrow}</span></div></Rise>
              <Rise delay={0.1}><h2 className="font-serif text-5xl lg:text-7xl leading-[0.95] tracking-tight">{t.insights.title1} <span className="italic-accent">{t.insights.title2}</span></h2></Rise>
            </div>
            <Rise delay={0.2}><Link to="/insights" className="btn-ghost">{t.insights.all} <ArrowUpRight size={14} strokeWidth={1.5} /></Link></Rise>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#141414] border border-[#141414]">
            {INSIGHTS.slice(0, 3).map((n, i) => (
              <Rise key={i} delay={i * 0.08}>
                <Link to={`/insights/${n.slug}`} className="block bg-[#050505] p-8 lg:p-10 h-full min-h-[380px] flex flex-col justify-between hover:bg-[#0A0A0A] transition-colors duration-500">
                  <div>
                    <div className="flex items-center justify-between chapter-num">
                      <span>{n.cat}</span>
                      <span>{n.read}</span>
                    </div>
                    <h3 className="mt-8 font-serif text-2xl lg:text-3xl leading-tight">{n.title}</h3>
                  </div>
                  <div className="mt-8 pt-6 border-t border-[#141414] flex items-center justify-between text-sm">
                    <span className="text-[#A19D94]">{n.author}</span>
                    <span className="text-[#66635C]">{n.date}</span>
                  </div>
                </Link>
              </Rise>
            ))}
          </div>
        </div>
      </section>

      {/* TRUSTED BY — real client logos */}
      <section data-testid="home-clients" className="py-20 border-b border-[#141414] overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 mb-12">
          <Rise>
            <div className="flex items-center gap-4">
              <span className="accent-bar" />
              <span className="eyebrow">{t.clients.eyebrow} · Trusted by</span>
            </div>
          </Rise>
        </div>
        <div className="marquee-mask">
          <div className="marquee-track items-center">
            {[...Array(2)].map((_, k) => (
              <div key={k} className="flex items-center gap-16 lg:gap-24 pr-16 lg:pr-24">
                {CLIENT_LOGOS.map((c, i) => (
                  <div
                    key={`${k}-${i}`}
                    className="shrink-0 h-16 lg:h-20 flex items-center justify-center px-4"
                    title={c.name}
                  >
                    <img
                      src={c.img}
                      alt={c.name}
                      loading="lazy"
                      className="max-h-16 lg:max-h-20 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-500"
                      style={{ filter: "grayscale(1) brightness(1.4) contrast(0.95)" }}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section data-testid={IDS.home.contactCta} className="py-32 lg:py-48">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-8">
              <Rise>
                <h2 className="font-serif text-6xl lg:text-[9vw] leading-[0.9] tracking-tight">
                  {t.contactCTA.title1} <span className="italic-accent">{t.contactCTA.title2}</span>
                </h2>
              </Rise>
              <Rise delay={0.1}>
                <p className="mt-8 text-[#A19D94] text-lg max-w-2xl leading-relaxed">{t.contactCTA.lead}</p>
              </Rise>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Rise delay={0.2}>
                <Link to="/contact" className="btn-primary text-base">
                  {t.contactCTA.cta}
                  <ArrowUpRight size={16} strokeWidth={1.5} />
                </Link>
              </Rise>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ParallaxBreak({ img }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-15%", "15%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.1, 1]);
  return (
    <section ref={ref} className="relative h-[80vh] overflow-hidden border-b border-[#141414]">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={img} alt="RPLC chambers" className="w-full h-full object-cover photo-mono" />
        <div className="absolute inset-0 spotlight" />
      </motion.div>
      <div className="relative z-10 h-full flex items-center px-6 lg:px-12">
        <div className="max-w-4xl">
          <MaskLine delay={0.1}>
            <span className="font-serif text-5xl lg:text-7xl leading-[0.95] tracking-tight italic-accent text-[#F4F1EB]">
              &ldquo;Great counsel is written on the margins.&rdquo;
            </span>
          </MaskLine>
        </div>
      </div>
    </section>
  );
}
